import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { Mesh, sortByPreferredOrder } from '~lmo/models/mesh.model';
import { Mine } from '~lmo/models/mine.model';
import { LmoFracsService, LmoReportsService } from '~lmo/services';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';

interface ReportFormValue {
  available: boolean;
  description: string;
  eta: number;
  mesh: Mesh;
  mine: Mine;
}

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-loader-status-report',
  styleUrls: ['./lmo-loader-status-report.component.scss'],
  templateUrl: './lmo-loader-status-report.component.html',
})
export class LmoLoaderStatusReportComponent implements OnInit {
  public reportForm: FormGroup;
  public isLoading = false;
  public inUseLoaders$: Observable<Mine[]>;
  public meshes$$: BehaviorSubject<Mesh[]> = new BehaviorSubject<Mesh[]>([]);
  constructor(
    private fb: FormBuilder,
    private reportService: LmoReportsService,
    private lmoModal: LmoFracModalService,
    private fracService: LmoFracsService,
  ) {}

  public ngOnInit() {
    this.inUseLoaders$ = this.fracService.currentFrac$.pipe(map((frac) => (frac ? frac.fracMines : [])));
    this.reportForm = this.fb.group({
      available: [false, [Validators.required]],
      description: [''],
      eta: [null, [Validators.required]],
      mesh: [null, [Validators.required]],
      mine: [null, [Validators.required]],
    });

    this.toggleDownMode();
    this.listenForMineChanges();

    this.reportForm.get('available').valueChanges.subscribe((available) => {
      if (available) {
        this.toggleUpMode();
      } else {
        this.toggleDownMode();
      }
    });
  }

  public createReport() {
    if (this.reportForm.invalid) {
      return;
    }

    this.isLoading = true;

    const formValue: ReportFormValue = this.reportForm.value;
    this.reportService
      .createSiteReport$({
        available: formValue.available,
        description: formValue.description,
        eta: formValue.eta * 60 /* send in minutes */,
        meshId: formValue.mesh.id,
        sendToReload: false,
        siteId: formValue.mine.site.id,
      })
      .subscribe(() => {
        this.isLoading = false;
        this.lmoModal.closeHomeModal();
      });
  }

  public mineDisplayFn(mine: Mine): string {
    return mine && mine.site ? mine.site.name : null;
  }

  private listenForMineChanges() {
    this.reportForm.get('mine').valueChanges.subscribe((mine: Mine) => {
      if (mine && mine.meshes) {
        const meshes = sortByPreferredOrder(mine.meshes);
        this.meshes$$.next(meshes);
      } else {
        this.meshes$$.next([]);
      }
    });
  }

  private toggleDownMode() {
    const descriptionControl = this.reportForm.get('description') as FormControl;
    descriptionControl.enable();

    const etaControl = this.reportForm.get('eta') as FormControl;
    etaControl.enable();
    etaControl.setValidators(Validators.required);
  }

  private toggleUpMode() {
    const descriptionControl = this.reportForm.get('description') as FormControl;
    descriptionControl.disable();

    const etaControl = this.reportForm.get('eta') as FormControl;
    etaControl.disable();
    etaControl.clearValidators();
  }
}
